import { Link, Stack, styled, Typography } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import React, { useRef } from 'react';
import { StyledParams } from 'types/common';
import { useInjection } from './hook';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { TFilePhoto } from 'types/user';
import theme from 'styles/theme';
import QuickNoteSection from '../../QuickNoteSection';
import DebtorDetailSection from '../../DebtorDetailSection';
import InsuranceDetailSection from '../../InsuranceDetailSection';
import CostDetailSection from '../../CostDetailSection';
import NextPaymentSection from '../../NextPaymentSection';

export type FormPostSeizure = {
  fileId?: number;
  quickNote?: string;

  debtorDetail: {
    name?: string;
    address?: string;
    otherAddress?: string;
    phoneNumber?: string;
    email?: string;
    employer?: string;
    employerPhone?: string;
    vin?: string;
    fileNumber?: string;
  };

  insuranceDetail: {
    provider?: string;
    policyNumber?: string;
    expiry?: Date | null;
  };

  costDetail: {
    bailiffFees?: number;
    redemptionFees?: number;
    mvsFees?: number;
    arrears?: number;
    arrearsTo?: string;
    clientFees?: number;
    transportFees?: number | string;
    auctionFees?: number | string;
    otherFeesIncurred?: string;
    otherFees?: number;
  };

  nextPaymentDetail: {
    nextPaymentDate?: Date | null;
    installmentAmount?: number;
    frequency?: string;
  };

  documents?: TFilePhoto[];
  fileDocumentAdd?: File[];
  // status: 'OPEN' | 'COMPLETED' | 'FAILED';
};
export type FormPostSeizureProps = {
  onSubmitForm: (value: FormPostSeizure) => void;
  asyncHandleGenerate: (value: FormPostSeizure) => void;
  isEdit?: boolean;
  handleMoveToArchive?: () => void;
  initDataForm?: Partial<FormPostSeizure>;
  isPre?: boolean;
};
export const PostSeizureForm = ({
  onSubmitForm,
  initDataForm,
  asyncHandleGenerate,
  isEdit,
  isPre
}: FormPostSeizureProps) => {
  const {
    methods,
    setIsShowModal,
    isShowModal,
    documents,
    fileDocumentAdd,
    handleDeleteDocument,
    handleDeleteFileAdd,
    handleFileChange,
    optionsAddress,
    isShowModalCompleted,
    setIsShowModalCompleted,
    isShowModalFail,
    setIsShowModalFail,
    handleConfirmCompleted,
    onViewFile,
    handleConfirmFailed
  } = useInjection({ initDataForm, isEdit, isPre });
  const fileInputDocumentRef = useRef<HTMLInputElement | null>(null);
  return (
    <FormProvider {...methods}>
      <div>
        <Wrapper>
          <QuickNoteSection onViewFile={onViewFile} />
          <DebtorDetailSection optionsAddress={optionsAddress} />
          <InsuranceDetailSection />
          <CostDetailSection />
          <NextPaymentSection />
          <Stack
            mt={5}
            flexDirection={'row'}
            alignItems={'flex-start'}
            justifyContent={'space-between'}>
            <Stack gap={1.25}>
              <Stack gap={1.25}>
                <ButtonCustom
                  onClick={() => {
                    if (fileInputDocumentRef.current) {
                      fileInputDocumentRef.current?.click();
                    }
                  }}
                  style={{ width: '240px', height: '50px' }}
                  label={'ADD DOCUMENTS'}
                />
                <Typography fontWeight={500} mb={1} fontSize={'13px'} color={theme.colors.dark1}>
                  Documents
                </Typography>
                {documents &&
                  documents.map((doc, index) => (
                    <Stack
                      key={index}
                      flexDirection={'row'}
                      alignItems={'center'}
                      width="fit-content"
                      justifyContent={'space-between'}>
                      <Stack
                        flexDirection={'row'}
                        gap={1.5}
                        marginRight="24px"
                        alignItems={'center'}>
                        <img src={'/icons/icon_document.svg'} alt={'icon'} />
                        <NameDocument href={doc.url} target={'_blank'}>
                          {doc.name}
                        </NameDocument>
                      </Stack>
                      <img
                        onClick={() => handleDeleteDocument(doc)}
                        style={{ cursor: 'pointer' }}
                        src={'/icons/icon_delete_file.svg'}
                        alt={'icon'}
                      />
                    </Stack>
                  ))}

                <Typography
                  fontWeight={500}
                  mt={2}
                  mb={1}
                  fontSize={'13px'}
                  color={theme.colors.dark1}>
                  Newly Added Files
                </Typography>
                {fileDocumentAdd &&
                  fileDocumentAdd.map((file, index) => (
                    <Stack
                      key={index}
                      flexDirection={'row'}
                      alignItems={'center'}
                      width="fit-content"
                      justifyContent={'space-between'}>
                      <Stack
                        flexDirection={'row'}
                        gap={1.5}
                        marginRight="24px"
                        alignItems={'center'}>
                        <img src={'/icons/icon_document.svg'} alt={'icon'} />
                        <Typography fontSize={13} fontWeight={500} color={theme.colors.dark1}>
                          {file.name}
                        </Typography>
                      </Stack>
                      <img
                        onClick={() => handleDeleteFileAdd(file)}
                        style={{ cursor: 'pointer' }}
                        src={'/icons/icon_delete_file.svg'}
                        alt={'icon'}
                      />
                    </Stack>
                  ))}
              </Stack>

              <input
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                ref={fileInputDocumentRef}
                hidden
                onChange={handleFileChange}
                type="file"
                multiple
              />
            </Stack>
            <Stack gap={1.25}>
              <ButtonCustom
                onClick={methods.handleSubmit(onSubmitForm)}
                style={{ width: '100%', height: '50px' }}
                label={'SAVE'}
              />
              <ButtonCustom
                onClick={methods.handleSubmit(asyncHandleGenerate)}
                style={{ width: '100%', height: '50px' }}
                label={'GENERATE STATEMENT'}
              />
              <Stack flexDirection={'row'} gap={2}>
                <ButtonCustom
                  isCompleted
                  onClick={() => setIsShowModalCompleted(true)}
                  style={{ width: '160px', height: '50px' }}
                  label={'COMPLETED'}
                />
                <ButtonCustom
                  onClick={() => setIsShowModalFail(true)}
                  isDelete
                  style={{ width: '160px', height: '50px' }}
                  label={'FAILED'}
                />
              </Stack>
            </Stack>
          </Stack>
          <DialogConfirm
            isOpen={isShowModal}
            onClose={() => setIsShowModal(false)}
            onSubmit={() => {}}
            textTitle={'Are you sure?'}
            textButton={'Confirm'}
            textContent={`This action will change the redemption status to `}
            contentStyle={{ textAlign: 'center' }}
          />
          <DialogConfirm
            isOpen={isShowModalCompleted}
            onClose={() => setIsShowModalCompleted(false)}
            onSubmit={handleConfirmCompleted}
            textTitle={'Are you sure?'}
            textButton={'Confirm'}
            textContent={
              'Are you sure you want to mark this as Completed and change the status to Close Redeemed?'
            }
            contentStyle={{ textAlign: 'center' }}
          />
          <DialogConfirm
            isOpen={isShowModalFail}
            onClose={() => setIsShowModalFail(false)}
            onSubmit={handleConfirmFailed}
            textTitle={'Are you sure?'}
            textButton={'Confirm'}
            textContent={
              isPre
                ? 'Are you sure you want to mark this as Failed? The file will be reopened.'
                : 'Are you sure you want to mark this as Failed? The file will be moved back to archive.'
            }
            contentStyle={{ textAlign: 'center' }}
          />
        </Wrapper>
      </div>
    </FormProvider>
  );
};

export const Wrapper = styled(Stack)(() => ({
  padding: '40px 30px',
  border: '1px solid #ccc',
  borderRadius: '20px',
  gap: '30px',
  maxWidth: '1100px'
}));

const NameDocument = styled(Link)(({ theme }: StyledParams) => ({
  color: theme.colors.dark1,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 'calc(100% - 24px)'
}));
